import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getMallList } from 'app/api/mall';
import { getStoreListOnMallID, uplaodMedia } from 'app/api/store';
import { getDefaultTierByMallId } from 'app/api/tier';
import {
  addVoucher,
  getVoucherById,
  updateVoucher,
  getVoucherTermsAndConditionsList,
} from 'app/api/voucher';
import {
  validate,
  voucherValidationRules,
  Messages,
} from 'app/constants/validation-constants';
import Message from 'app/utils/Message';
import { dataformatChangesYearToDate } from 'app/utils/Parsers';
import Toast from 'app/utils/Toast';

// D-06/D-15: Accrual is a legitimate tier value of zero, offered in this
// form's tier dropdown ONLY for game vouchers (D-06) — never a real
// `tier_master` row, and never injected server-side into the shared
// getDefaultTierByMallId endpoint (D-15, which has 4 other consumers where
// an Accrual option is meaningless). This is a deliberate SECOND client-side
// declaration of the sentinel, not a cross-feature import from the campaign
// builder's submitHelpers.js — D-15 asks for the option to be added per
// form. The shared name ACCRUAL_TIER_ID is what keeps the D-13 grep sweep
// meaningful across both repos. Value is the STRING form of the sentinel
// because every other entry in this dropdown is String()-coerced and
// values.tierIds is hydrated with .map(String).
const ACCRUAL_TIER_ID = 0;
const ACCRUAL_VOUCHER_OPTION = { value: String(ACCRUAL_TIER_ID), name: 'Accrual' };

const useCreateVoucher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tierList, setTierList] = useState([]);
  const [mallList, setMallList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [voucherTermsConditions, setVoucherTermsConditions] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);

  const [error, setErrors] = useState({});
  const [values, setValues] = useState({
    voucherTitle: '',
    description: '',
    mallId: null,
    storeId: '',
    tierIds: [],
    promotionalImage: '',
    usageLimit: '',
    loyaltyPoints: '',
    frequency: '',
    termsOfUsage: [],
    voucherCount: '',
    sortOrder: '',
    voucherType: '',
    howToRedeem: '',
    visible: true,
    status: true,
    startDate: '',
    endDate: '',
    isGamificationVoucher: false,
    // Quick task 260903-fdd (D-1): the payload is built by spreading
    // ...values (see callAddOrUpdateAPI), so this one line is also the
    // entire create/update wiring for the flag.
    isSpecialVoucher: false,
  });

  const storeId = location?.state?.id ? location?.state?.id : '';

  async function getMallListforDropdown() {
    setPageLoading(true);
    let response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.malls?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      setMallList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  async function getStoreListDropdown(value) {
    setPageLoading(true);
    let response = await getStoreListOnMallID(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.storeName,
        };
      });
      setStoreList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  async function getTierListDropdown(value) {
    setPageLoading(true);
    let response = await getDefaultTierByMallId(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = response?.data?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      setTierList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  async function getVoucherTermsAndConditionsDropdown(value) {
    setPageLoading(true);
    let response = await getVoucherTermsAndConditionsList(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = response?.data?.map((item, index) => {
        return {
          ...item,
          value: item.label,
          name: item.label,
        };
      });
      setVoucherTermsConditions(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  async function loadStoreData(id) {
    if (!id) {
      return;
    }
    setPageLoading(true);
    let response = await getVoucherById(id);
    setPageLoading(false);
    if (response?.responseCode === 200) {
      let responsedata = response?.data;
      // Reconcile tier shape for the multi-select. A voucher with no tier
      // restriction (tierIds empty/[]/[null] and no scalar tierId) is an
      // "All tiers" voucher -> hydrate the hardcoded "All" sentinel so it
      // renders a chip and passes the mandatory tier validation on re-save.
      const rawTierIds = Array.isArray(responsedata?.tierIds)
        ? responsedata.tierIds.filter((t) => t != null)
        : [];
      const hydratedTierIds =
        rawTierIds.length > 0
          ? rawTierIds.map(String)
          : responsedata?.tierId != null
          ? [String(responsedata.tierId)]
          : ['ALL'];
      setValues((prevState) => ({
        ...responsedata,
        tierIds: hydratedTierIds,
        endDate: dataformatChangesYearToDate(responsedata?.expiryDate),
        startDate: dataformatChangesYearToDate(responsedata?.startDate),
      }));
      await getStoreListDropdown(responsedata?.mallId);
      await getTierListDropdown(responsedata?.mallId);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }

  async function callAddOrUpdateAPI() {
    setPageLoading(true);
    // "All" (sentinel) => no tier restriction: send an empty array so the
    // backend persists tierIds = [] / tierId = null and the reserve gate
    // fail-opens (only usable points are checked).
    const isAllTiers = (values.tierIds || []).includes('ALL');
    let senddata = {
      ...values,
      expiryDate: values?.endDate,
      tierIds: isAllTiers
        ? []
        : [...new Set((values.tierIds || []).map(Number))],
    };
    delete senddata.tierId;
    delete senddata.endDate;
    delete senddata.updatedAt;
    let response = {};
    if (storeId) {
      response = await updateVoucher(storeId, { ...senddata });
    } else {
      response = await addVoucher({ ...senddata });
    }
    setPageLoading(false);
    if (response?.responseCode === 200) {
      Toast.success(
        storeId
          ? Message.Voucher.UPDATED_SUCCESS
          : Message.Voucher.CREATED_SUCCESS
      );
      navigate(-1);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }

  function handleCreateVoucher(e) {
    e.preventDefault();
    const validationErrors = validate(values, voucherValidationRules);

    // A non-game (catalog) voucher is claimed by spending loyalty points, so it
    // must cost > 0. A 0-point non-game voucher makes the reserve gate
    // (usablePoints >= price) trivially true, letting even an accrual user with
    // 0 usable points reserve it. Game vouchers are exempt (won by playing;
    // backend forces their points to 0).
    if (!values.isGamificationVoucher && !validationErrors.loyaltyPoints) {
      const points = Number(values.loyaltyPoints);
      if (!Number.isFinite(points) || points <= 0) {
        validationErrors.loyaltyPoints = Messages.LOYALTY_POINTS_POSITIVE;
      }
    }

    // The hardcoded "All" tier option submits tierIds = [] (no tier
    // restriction). That is only meaningful for a REGULAR voucher: the
    // backend's reserve gate deliberately fail-opens for regular vouchers
    // whose tier ids resolve to zero known concepts, but a gamification
    // voucher takes the deny-on-unknown branch (D-05/T-08-01 in
    // VoucherService) and is rejected at claim time for EVERY member. So a
    // game voucher saved with "All" is a permanently unclaimable voucher,
    // and no server-side guard currently blocks it — the mirror-image rule
    // (Accrual implies game voucher) is enforced server-side, this one is
    // not. Block it here instead of persisting dead data.
    //
    // Only "All" is restricted: a game voucher may still multi-select
    // Accrual and/or any specific tiers. Never strip 'ALL' from
    // values.tierIds to "fix" the selection silently — 260717-k0z is the
    // standing precedent for why silent stripping is the anti-pattern.
    const isAllTiersSelected = (values.tierIds || []).includes('ALL');
    if (values.isGamificationVoucher && isAllTiersSelected) {
      validationErrors.tierIds = Messages.ALL_TIER_REGULAR_ONLY;
    }

    // D-5 (quick task 260903-fdd): Special Voucher and Reserve for Game are
    // mutually exclusive — game vouchers are excluded from the app catalog
    // entirely, so a special game voucher would have no listing for the
    // flag to affect. Inline error only, mirroring the All-tier guard above;
    // never flip either toggle off automatically (260717-k0z no-silent-repair
    // precedent) — the admin fixes it by hand.
    if (values.isSpecialVoucher && values.isGamificationVoucher) {
      validationErrors.isSpecialVoucher = Messages.SPECIAL_AND_GAME_EXCLUSIVE;
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      callAddOrUpdateAPI();
    }
  }

  function handleChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]:
          name === 'usageLimit' ||
          name === 'loyaltyPoints' ||
          name === 'frequency' ||
          name === 'voucherCount' ||
          name === 'sortOrder'
            ? parseInt(value)
            : value,
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
        });
      }
    }
  }
  function handleTierChange(e) {
    if (!(e && e.target)) {
      return;
    }
    const incoming = e.target.value || [];
    const prev = values.tierIds || [];
    const allJustAdded = incoming.includes('ALL') && !prev.includes('ALL');
    let next;
    if (allJustAdded) {
      // Choosing "All" clears any specific tiers (mutually exclusive).
      next = ['ALL'];
    } else if (incoming.includes('ALL') && incoming.length > 1) {
      // A specific tier was added while "All" was selected -> drop "All".
      next = incoming.filter((v) => v !== 'ALL');
    } else {
      next = incoming;
    }
    setValues({
      ...values,
      tierIds: next,
    });
    if (error.tierIds) {
      setErrors({
        ...error,
        tierIds: '',
      });
    }
  }
  function setChange(name, val) {
    setValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
    setErrors({
      ...error,
      [name]: '',
      // The All-tier rule is cross-field (tierIds x isGamificationVoucher),
      // so flipping the Reserve for Game toggle can resolve a tierIds error
      // that the tier dropdown itself never sees a change event for. Clear
      // it here too, otherwise a now-valid selection keeps a stale inline
      // message. Submit is unaffected either way — setErrors replaces the
      // whole object on every submit and the guard is re-evaluated there.
      //
      // D-5 (quick task 260903-fdd): the Special x Game rule is likewise
      // cross-field. Flipping EITHER toggle can resolve it, but only one of
      // them fires this change event — flipping isGamificationVoucher must
      // also clear a stale isSpecialVoucher error. Flipping isSpecialVoucher
      // itself is already covered by the generic [name]: '' above.
      ...(name === 'isGamificationVoucher'
        ? { tierIds: '', isSpecialVoucher: '' }
        : {}),
    });
  }
  async function uploadMediaHandler(file, name) {
    if (!file) {
      setChange(name, null);
      return;
    }
    const response = await uplaodMedia(file);
    if (response.responseCode === 200) {
      Toast.success('Image upload Successfull');

      const data = await response.data;
      setChange(name, data?.url);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }
  async function handleTime(value, name) {
    setValues({
      ...values,
      [name]: value,
    });

    if (error[name]) {
      setErrors({
        ...error,
        [name]: '',
      });
    }
  }
  function handleMallChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setStoreList([]);
      setTierList([]);

      getStoreListDropdown(value);
      getTierListDropdown(value);

      setValues({
        ...values,
        [name]: value,
        storeId: '',
        tierIds: [],
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
          storeId: '',
          tierIds: '',
        });
      }
    }
  }
  useEffect(() => {
    getMallListforDropdown();
    getVoucherTermsAndConditionsDropdown();
    if (storeId) {
      loadStoreData(storeId);
    }
  }, []);

  // D-06: the tier dropdown's contents are conditional on voucher kind —
  // Accrual is offered unconditionally, and the save-time server guard is the
  // only thing that enforces "Accrual implies game voucher" (D-06 revised
  // 2026-08-13). It was previously gated on values.isGamificationVoucher, but
  // the "Reserve for Game" toggle renders in the toggle group near the bottom
  // of index.js while this dropdown sits ~10 fields above it: an admin filling
  // the form top-to-bottom reached the tier field before the switch that
  // revealed the option, so Accrual looked missing and required scrolling back
  // up to re-open the dropdown. The backend's D-07 guard (Plan 03) already
  // rejects a non-game voucher carrying Accrual with an explicit message and
  // persists no row, and every save path here surfaces response.message via
  // Toast.error — so hiding the option bought no safety, only the discovery
  // problem. Never filter/strip values.tierIds here — the server owns that rule.
  const tierIdsOptions = useMemo(
    () => [
      // Hardcoded "All" option: no tier restriction (any tier at this mall
      // with usable points can reserve). Mutually exclusive with specific
      // tiers; submitted as tierIds = [].
      { value: 'ALL', name: 'All' },
      ACCRUAL_VOUCHER_OPTION,
      ...tierList.map((t) => ({
        ...t,
        value: String(t.value),
        name: t.name ?? t.label,
      })),
    ],
    [tierList]
  );

  return {
    handleMallChange,
    tierList,
    tierIdsOptions,
    storeList,
    mallList,
    handleChange,
    handleTierChange,
    values,
    error,
    setValues,
    handleCreateVoucher,
    pageLoading,
    storeId,
    uploadMediaHandler,
    navigate,
    setChange,
    handleTime,
    voucherTermsConditions,
  };
};

export default useCreateVoucher;
